import React, { useContext, useState, useRef, useEffect } from 'react';
import QRCode, { QRCodeSVG } from 'qrcode.react';
import { EventContext } from '../context/EventContext';
import logo from '../assets/logo.png';
import { getLogoDownloadUrl } from '../services/logoService';
import { ROUTES } from '../constants/routes';
import { useToast } from '../context/ToastContext';


const QRComponent: React.FC = () => {
  const { currentEvent } = useContext(EventContext);
  const [mode, setMode] = useState<string>(ROUTES.CYNE_UP);
  const printRef = useRef<HTMLDivElement>(null);
  const [logoUrl, setLogoUrl] = useState('');
  const { addToast } = useToast();



  useEffect(() => {
    const fetchLogoUrl = async () => {
      if (currentEvent && currentEvent.logo != '') {
        try {
          if (currentEvent.logo) {
            const result = await getLogoDownloadUrl(currentEvent.event_id, currentEvent.logo);
            if (result) {
              setLogoUrl(result);
            }
          }
        } catch (error) {
          console.error('Error fetching logo download URL:', error);
        }
      }
    };
    fetchLogoUrl();
  }, [currentEvent]);

  if (!currentEvent) {
    return <p>Loading event details...</p>;
  }

  const copyQRCodeToClipboard = async () => {
    if (!printRef.current) return;

    const svg = printRef.current.querySelector('svg');
    if (!svg) {
      console.error('SVG not found');
      return;
    }

    // Serialize SVG to string
    const svgString = new XMLSerializer().serializeToString(svg);
    const blob = new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(blob);

    // Create image from SVG blob
    const img = new Image();
    img.src = url;

    img.onload = async () => {
      // Create canvas to draw image
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;

      const ctx = canvas.getContext('2d');
      ctx?.drawImage(img, 0, 0);

      // Convert to PNG blob
      canvas.toBlob(async (pngBlob) => {
        if (!pngBlob) {
          console.error('Failed to convert canvas to blob');
          return;
        }

        try {
          await navigator.clipboard.write([
            new ClipboardItem({
              [pngBlob.type]: pngBlob
            })
          ]);
          addToast('QR Code Copied to Clipboard!')
        } catch (err) {
          console.error('Clipboard write failed', err);
          addToast('Copy to clipboard failed. Try a different browser.', true);
        }
      }, 'image/png');
    };
  };

  const { event_name, event_code } = currentEvent;
  const qrValue = `${window.location.origin}${mode}/${event_code}`;
  const label = mode === ROUTES.CYNE_UP ? 'Cyne Up' : 'Check In';
  const url = `${window.location.origin}${mode}`;

  const handleToggleMode = () => {
    setMode(prev => (prev === ROUTES.CYNE_UP ? ROUTES.CHECKIN : ROUTES.CYNE_UP));
  };

  const handlePrint = () => {
    if (printRef.current) {
      const printContents = printRef.current.innerHTML;
      const printWindow = window.open('', '', 'height=600,width=800');
      if (printWindow) {
        printWindow.document.write('<html><head><title>Print QR</title>');
        printWindow.document.write(`
  <style>
    * {
      box-sizing: border-box;
    }

    body {
      margin: 0;
      padding: 20px;
      font-family: sans-serif;
      text-align: center;
    }

    img {
      max-width: 100%;
      height: auto;
    }

    .logo {
      max-width: 300px;
      height: auto;
      margin: 0 auto 10px auto;
    }

    .event-logo {
      max-width: 40px;
      height: 40px;
      border-radius: 50%;
      margin-right: 10px;
    }

    .qr {
      margin: 20px 0;
    }

    h1, h2, p {
      margin: 10px 0;
    }

    a {
      word-break: break-word;
    }
  </style>
`);
        printWindow.document.write('</head><body>');
        printWindow.document.write(printContents);
        printWindow.document.write('</body></html>');

        printWindow.document.close();

        // Delay to allow images and SVG to load
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
          }, 500); // 0.5 second delay to ensure SVGs/images are rendered
        };
      }
    }
  };

  return (
    <div style={styles.container}>
      <div ref={printRef} style={styles.content}>
        <img src={logo} className="logo" alt="Cyne Up" style={{ maxWidth: '400px', marginTop: '-20px', marginBottom: '0px', marginLeft: '-10px' }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img src={logoUrl} className="event-logo" style={styles.eventLogo} />
          <h1 style={{ margin: '0px' }}>{event_name}</h1>
        </div>
        <h2 style={{ margin: '10px' }}>Code: {event_code}</h2>
        <div style={styles.qr}>
          <QRCodeSVG value={qrValue} size={350} />
        </div>
        <h2 style={{ margin: '0px' }}>{label}</h2>
        <p>Or visit <a href="{{url}}">{url}</a> and enter code {currentEvent.event_code}</p>
      </div>

      <div style={styles.buttons} className="no-print">
        {currentEvent.has_checkin && (<button onClick={handleToggleMode} style={{ minWidth: '200px' }}>
          Switch to {mode === ROUTES.CYNE_UP ? 'Check In' : 'Cyne Up'}
        </button>)}
        <button onClick={copyQRCodeToClipboard} style={{ minWidth: '200px' }}>
          Copy QR as Image
        </button>
        <button onClick={handlePrint} style={{ minWidth: '200px' }} disabled={currentEvent?.is_locked}>Print</button>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: {
    height: '80vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'start',
    alignItems: 'center',
    margin: '0px',
    marginBottom: '10px'
  },
  content: {
    textAlign: 'center',
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  qr: {
    margin: '1rem 0',
  },
  buttons: {
    display: 'flex',
    gap: '1rem',
    marginTop: '0rem',
  },
  eventLogo: {
    maxWidth: '40px',
    maxHeight: '40px',
    height: '40px',
    borderRadius: '50%'
  },
};

// Hide buttons when printing
const style = document.createElement("style");
style.innerHTML = `
  @media print {
    .no-print {
      display: none !important;
    }
  }
`;
document.head.appendChild(style);

export default QRComponent;
